import { useEffect, useRef, useState } from "react";
import { useInView } from "framer-motion";
import { IdCard, Globe2, PiggyBank } from "lucide-react";
import { formatInline } from "@/lib/inline";

const CountUp = ({ end = 8000, duration = 2000 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(Math.round(eased * end));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, end, duration]);

  return (
    <span ref={ref} data-testid="counter-value">
      {value}
    </span>
  );
};

const IconBadge = ({ children }) => (
  <div className="mb-7 inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-[#2f68b0] text-white shadow-lg shadow-[#2f68b0]/25">
    {children}
  </div>
);

const cardClass =
  "flex flex-col rounded-3xl bg-white p-8 ring-1 ring-slate-100 shadow-[0_18px_50px_-24px_rgba(20,60,120,0.35)] transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_28px_60px_-24px_rgba(46,163,165,0.45)]";

const Hashtag = ({ text }) =>
  text ? <span className="italic font-semibold text-[#ff0000]">{text}</span> : null;

const ICONS = [IdCard, Globe2, PiggyBank];

export const InfoColumns = ({ info }) => {
  if (!info || info.length === 0) return null;
  return (
    <section className="bg-[#f4f7fb] py-16 md:py-24" data-testid="info-columns-section">
      <div className="mx-auto grid max-w-[1150px] grid-cols-1 gap-6 px-6 md:grid-cols-3">
        {info.map((item, i) => {
          const Icon = ICONS[i] || IdCard;
          return (
            <div className={cardClass} key={item.hashtag || i} data-testid={`info-card-${i}`}>
              <IconBadge>
                <Icon size={30} strokeWidth={1.9} />
              </IconBadge>
              {item.ron != null && (
                <div className="mb-4 flex items-baseline gap-2">
                  <span className="text-5xl font-extrabold leading-none text-[#2f68b0]">
                    <CountUp end={item.ron} />
                  </span>
                  <span className="text-xl font-bold text-slate-400">RON</span>
                </div>
              )}
              <p className="text-[15px] leading-7 text-slate-600">
                {formatInline(item.text)} <Hashtag text={item.hashtag} />
              </p>
            </div>
          );
        })}
      </div>
    </section>
  );
};
